import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { LogOut, Home } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import logo from "@/assets/skylink-logo.png";
import Footer from "./Footer";
import { NotificationBell } from "./NotificationBell";
import { GlobalSearch } from "./GlobalSearch";
import { useUserRole } from "@/hooks/useUserRole";
import { useKeyboardShortcuts } from "@/hooks/useKeyboardShortcuts";

import { ThemeToggle } from "./ThemeToggle";

interface LayoutProps {
  children: React.ReactNode;
  userName?: string;
}

const Layout = ({ children, userName }: LayoutProps) => {
  const navigate = useNavigate();
  const { data: roles } = useUserRole();

  // Enable keyboard shortcuts
  useKeyboardShortcuts({ enabled: true });

  const handleLogout = async () => {
    await supabase.auth.signOut();
    toast.success("Logged out successfully");
    navigate("/auth");
  };

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <header className="border-b bg-card sticky top-0 z-50">
        <div className="container mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2 min-w-0 flex-shrink">
            <img src={logo} alt="Skylink" className="h-8 sm:h-10 flex-shrink-0" width="147" height="48" fetchPriority="high" />
            <h1 className="text-sm sm:text-xl font-bold truncate hidden xs:block">Management Portal</h1>
          </div>
          <div className="flex items-center gap-1 sm:gap-2">
            <GlobalSearch />
            {userName && (
              <p className="text-xs sm:text-sm text-muted-foreground hidden lg:block truncate max-w-[120px]">Welcome, {userName}</p>
            )}
            <ThemeToggle />
            {roles?.isSuperAdmin && <NotificationBell />}
            <Button variant="outline" size="sm" onClick={() => navigate("/dashboard")} className="hidden sm:flex">
              <Home className="h-4 w-4 mr-2" />
              Dashboard
            </Button>
            <Button variant="outline" size="sm" onClick={() => navigate("/dashboard")} className="sm:hidden">
              <Home className="h-4 w-4" />
            </Button>
            <Button variant="outline" size="sm" onClick={handleLogout} className="hidden sm:flex">
              <LogOut className="h-4 w-4 mr-2" />
              Logout
            </Button>
            <Button variant="outline" size="sm" onClick={handleLogout} className="sm:hidden">
              <LogOut className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </header>
      <main className="flex-1 container mx-auto px-4 py-4 sm:py-8">{children}</main>
      <Footer />
    </div>
  );
};

export default Layout;
